/**
 * Query Key Factory — SSoT for all React Query keys.
 *
 * Mirrors trade01's lib/queryKeys.ts discipline. Centralizes key management
 * to prevent mismatches across hooks, cache invalidation, and prefetching.
 *
 * Convention: each family exposes an `all` root array plus builder functions
 * that spread `...all` first, then a discriminator string, then params (with
 * `?? null` for optionals so shapes stay stable). Broad invalidation targets
 * the `all` root (or a `[...all, 'discriminator']` prefix). When swapping an
 * inline key for a factory call, keep the produced array byte-identical to the
 * old literal — a different shape silently busts the cache.
 */
export const queryKeys = {
  health: {
    all: ['health'] as const,
    root: () => [...queryKeys.health.all, 'root'] as const,
    readiness: () => [...queryKeys.health.all, 'readiness'] as const,
    circuitBreaker: (alias: string) =>
      [...queryKeys.health.all, 'circuit_breaker', alias] as const,
    ingestStatus: () => [...queryKeys.health.all, 'ingest_status'] as const,
  },
  scheduler: {
    all: ['scheduler'] as const,
    status: () => [...queryKeys.scheduler.all, 'status'] as const,
  },
  ws: {
    all: ['ws'] as const,
    status: () => [...queryKeys.ws.all, 'status'] as const,
  },
  capture: {
    all: ['capture'] as const,
    status: () => [...queryKeys.capture.all, 'status'] as const,
  },
  income: {
    all: ['income'] as const,
    dashboard: (scope: string) => [...queryKeys.income.all, 'dashboard', scope] as const,
    goals: () => [...queryKeys.income.all, 'goals'] as const,
    cycles: (g: string, scope: string, limit?: number) =>
      [...queryKeys.income.all, 'cycles', g, scope, limit ?? null] as const,
  },
  trade: {
    all: ['trade'] as const,
    positions: (alias?: string, assetClass?: string) =>
      [...queryKeys.trade.all, 'live-positions', alias ?? 'all', assetClass ?? 'all'] as const,
    orders: (alias?: string, includeFilled?: boolean) =>
      [...queryKeys.trade.all, 'live-orders', alias ?? 'all', includeFilled ?? false] as const,
  },
  charts: {
    all: ['charts'] as const,
    profiles: () => [...queryKeys.charts.all, 'profiles'] as const,
    symbols: () => [...queryKeys.charts.all, 'symbols'] as const,
    bars: (symbol?: string, start?: string, end?: string, interval?: string) =>
      [...queryKeys.charts.all, 'bars', symbol, start, end, interval ?? 'D'] as const,
    chain: (symbol: string | null, date: string | null) =>
      [...queryKeys.charts.all, 'chain', symbol, date] as const,
    replay: (symbol: string | undefined, start: string | undefined, end: string | undefined, bar: number | undefined, ordersHash: string) =>
      [...queryKeys.charts.all, 'replay', symbol, start, end, bar, ordersHash] as const,
  },
  gateways: {
    all: ['gateways'] as const,
    list: () => queryKeys.gateways.all,
    // NB: the per-gateway status key intentionally uses the singular 'gateway'
    // root (distinct from the list key) — invalidations target ['gateway','status'].
    status: (gatewayId: string | null) => ['gateway', 'status', gatewayId] as const,
    statusAll: () => ['gateway', 'status'] as const,
  },
  brokerAccounts: {
    all: ['broker_accounts_crud'] as const,
  },
  backtestRuns: {
    all: ['backtestRuns'] as const,
    list: (limit: number) => [...queryKeys.backtestRuns.all, 'list', limit] as const,
    detail: (id: string) => [...queryKeys.backtestRuns.all, 'detail', id] as const,
  },
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    list: () => [...queryKeys.tenants.all, 'list'] as const,
    detail: (tenantId: string | null) => [...queryKeys.tenants.all, 'detail', tenantId] as const,
    audit: (tenantId: string | null) => [...queryKeys.tenants.all, 'audit', tenantId] as const,
    options: () => ['tenant_options'] as const,
  },
  screener: {
    all: ['screener'] as const,
    meta: () => [...queryKeys.screener.all, 'meta'] as const,
    watchlists: () => [...queryKeys.screener.all, 'watchlists'] as const,
  },
  aggregated: {
    all: ['aggregated'] as const,
    positions: (assetClass?: string) =>
      [...queryKeys.aggregated.all, 'positions', assetClass ?? null] as const,
    risk: () => [...queryKeys.aggregated.all, 'risk'] as const,
  },
  plans: {
    all: ['plans'] as const,
    list: (lifecycle?: string, accountAlias?: string) =>
      [...queryKeys.plans.all, 'list', lifecycle ?? null, accountAlias ?? null] as const,
    detail: (planId: string, version?: number) =>
      [...queryKeys.plans.all, 'detail', planId, version ?? null] as const,
    state: (planId: string) => [...queryKeys.plans.all, 'state', planId] as const,
    events: (planId: string, limit?: number) =>
      [...queryKeys.plans.all, 'events', planId, limit ?? 50] as const,
  },
  runs: {
    all: ['runs'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.runs.all, 'list', filters ?? null] as const,
    detail: (runId: string) => [...queryKeys.runs.all, 'detail', runId] as const,
  },
  baseSelector: {
    all: ['base-selector'] as const,
    profiles: () => [...queryKeys.baseSelector.all, 'profiles'] as const,
    quotes: (keys: string[]) => [...queryKeys.baseSelector.all, 'quotes', keys] as const,
  },
  risk: {
    all: ['risk'] as const,
    dashboard: () => [...queryKeys.risk.all, 'dashboard'] as const,
    assessment: (alias: string) => [...queryKeys.risk.all, 'assessment', alias] as const,
    positionMargins: (alias: string) => [...queryKeys.risk.all, 'position-margins', alias] as const,
  },
  optionsChain: {
    all: ['options-chain'] as const,
    expirations: (symbol: string, month?: string) =>
      [...queryKeys.optionsChain.all, 'expirations', symbol, month ?? 'front'] as const,
    quotes: (symbol: string, expiry: string, count: number, rights: string, month?: string,
      bandKey?: string, pinKey?: string) =>
      [...queryKeys.optionsChain.all, 'quotes', symbol, expiry, count, rights, month ?? 'front',
        bandKey ?? 'count', pinKey ?? ''] as const,
    atr: (symbol: string) => [...queryKeys.optionsChain.all, 'atr', symbol] as const,
    ivMetrics: (symbol: string) => [...queryKeys.optionsChain.all, 'iv-metrics', symbol] as const,
    expectedMove: (symbol: string) =>
      [...queryKeys.optionsChain.all, 'expected-move', symbol] as const,
  },
  structures: {
    all: ['structures'] as const,
    list: (alias?: string) => ['structures', 'list', alias ?? 'all'] as const,
  },
  execEvents: {
    all: ['exec-events'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.execEvents.all, 'list', filters ?? null] as const,
    detail: (eventId: number) => [...queryKeys.execEvents.all, 'detail', eventId] as const,
  },
  ledger: {
    all: ['ledger'] as const,
    positions: (filters?: Record<string, unknown>) =>
      [...queryKeys.ledger.all, 'positions', filters ?? null] as const,
    summary: (account?: string) => [...queryKeys.ledger.all, 'summary', account ?? null] as const,
    cash: (type?: string, account?: string) => [...queryKeys.ledger.all, 'cash', type ?? null, account ?? null] as const,
    importHistory: () => [...queryKeys.ledger.all, 'import-history'] as const,
    flexStatus: () => [...queryKeys.ledger.all, 'flex-status'] as const,
    ghosts: () => [...queryKeys.ledger.all, 'ghosts'] as const,
  },
  tracker: {
    all: ['tracker'] as const,
    list: (status?: string, account?: string) => [...queryKeys.tracker.all, 'list', status ?? null, account ?? null] as const,
    detail: (tradeId: number) => [...queryKeys.tracker.all, 'detail', tradeId] as const,
    equity: (tradeId: number) => [...queryKeys.tracker.all, 'equity', tradeId] as const,
    unassigned: (underlying: string, includeClosed: boolean, account?: string) =>
      [...queryKeys.tracker.all, 'unassigned', underlying, includeClosed, account ?? 'all'] as const,
    suggestions: (account?: string) =>
      [...queryKeys.tracker.all, 'suggestions', account ?? null] as const,
    livePnl: (tradeId: number | string) =>
      [...queryKeys.tracker.all, 'live-pnl', tradeId] as const,
  },
  settings: {
    all: ['settings'] as const,
    liveRefresh: () => [...queryKeys.settings.all, 'live-refresh'] as const,
  },
}
